<script setup>
import { useEventStore } from "@/stores/EventsStore"
import { useModalStore } from "@/stores/ModalStore"
import { showToast } from "@/utils/toast"
import { useProductsStore } from "@/stores/ProductsStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { formatNumber } from "@/utils/strings"
// import { VNumberInput } from "vuetify/labs/components"

const calendarStore = useCalendarStore()
const eventStore = useEventStore()
const modalStore = useModalStore()
const productStore = useProductsStore()

const { t } = useI18n()

const THIS_MODAL_NAME = 'editProduct'

const productName = ref('') // Name of the product being edited, initialized to an empty string
const price = ref(0) // Price of the product being edited, initialized to 0
const finalPrice = ref(0) // Final price after discount, initialized to 0
const quantity = ref(1) // Quantity of the product being edited, default is 1

const localDiscountType = ref('solid') // Local discount type for the product being edited
const localDiscountValue = ref(null) // Local discount type and value for the product being edited

const editedProductDynamicId = ref(null)

// 👉 Calculate value based on price and quantity
const calculateValue = (price, quantity) => {
  return productStore.calculateProductValue(price, quantity)
}

// 👉 Price after applying discount
const productFinalPrice = computed(() => {
  return productStore.calculateProductPrice(
    Number(price.value),
    Number(quantity.value),
    localDiscountType.value,
    localDiscountValue.value,
  )
})

// 👉 Save changes to the product
const saveChanges = async () => {
  console.log('Saving changes for product index:', editedProductDynamicId.value)
  try {
    const id = editedProductDynamicId.value

    if (id === null || id === undefined) {
      console.warn('No product dynamicId available')

      return
    }

    const productIndex = eventStore.selectedProducts.findIndex(
      (p) => p && p.dynamicId === id,
    )

    // Get the current product
    const currentProduct = eventStore.selectedProducts[productIndex]

    console.log('Saving changes for product index:', currentProduct)

    // Create an updated product with the new values
    const updatedProduct = {
      ...currentProduct,
      price: Number(price.value),
      quantity: Number(quantity.value),
      discountType: localDiscountType.value,
      discountValue: localDiscountValue.value,
    }

    // Apply all pricing calculations at once
    // Update the product in the array
    eventStore.selectedProducts[productIndex] = productStore.updateProductPricing(updatedProduct)

    modalStore.closeModal(THIS_MODAL_NAME)
    showToast(t('savedChanges'), 'success')
  } catch (e) {
    console.error('Error in saveChanges:', e)
    showToast(t('errorSavingChanges'), 'error')
  }
}

// 👉 Initialize dialog with product data
const initializeDialog = (product, idx) => {
  console.log('Initializing EditProductDialog with product:', product, idx)

  if (product && (idx !== undefined && idx !== null)) {
    productStore.setCurrentProduct(product)
    productName.value = product.title || product.name
    price.value = product.price
    quantity.value = product.quantity
    finalPrice.value = product.finalPrice || product.price
    editedProductDynamicId.value = product.dynamicId

    // Initialize discount values based on product data
    if (product.discountType) {
      // If product already has a discount type, use it
      localDiscountType.value = product.discountType
      localDiscountValue.value = product.discountValue || null
    } else {
      // Otherwise determine it from existing discount values
      if (product.discountType === 'solid' && Number(product.discountValue) > 0) {
        localDiscountType.value = 'solid'
        localDiscountValue.value = product.discountValue
      } else if (product.discountType === 'percent' && Number(product.discountValue) > 0) {
        localDiscountType.value = 'percent'
        localDiscountValue.value = product.discountValue
      } else {
        localDiscountType.value = 'solid'
        localDiscountValue.value = ''
      }
    }

  } else {
    console.error('InitializeDialog received no product.')
  }
}

const closeModalAndReset = () => {
  productName.value = ""
  price.value = 0
  quantity.value = 1
  localDiscountType.value = 'solid'
  localDiscountValue.value = null
  editedProductDynamicId.value = null
  finalPrice.value = 0
  modalStore.closeModal(THIS_MODAL_NAME)
}

defineExpose({
  initializeDialog,
})

//reset
onUnmounted(() => {
  productName.value = ""
  price.value = 0
  quantity.value = 1
  localDiscountType.value = 'solid'
  localDiscountValue.value = ''
  editedProductDynamicId.value = null,
  finalPrice.value = 0
})
</script>

<template>
  <VDialog
    v-model="modalStore.modals.editProduct"
    max-width="350px"
  >
    <VCard>
      <VCardTitle>
        <div class="d-flex pa-1 justify-space-between align-center">
          {{ $t('discount') }}
          <VIcon
            icon="ri-close-line"
            @click="closeModalAndReset"
          />
        </div>
      </VCardTitle>
      <VCardText class="pt-0">
        <VRow>
          <VCol cols="12">
            {{ $t('Product') }}:
            <span class="font-weight-bold">
              {{ productName }}
            </span>
          </VCol>
          <VCol cols="12">
            {{ $t('value') }}:
            <span data-testid="discountModal-product-value">
              {{ formatNumber(calculateValue(price, quantity)) }}&nbsp;{{ calendarStore.currency }}
            </span>
          </VCol>
          <VCol cols="12">
            {{ $t('discount_type') }}:
            <VRadioGroup
              v-model="localDiscountType"
              inline
              hide-details
              class=""
              @update:model-value="() => {
                if(localDiscountType === 'solid' && Number(localDiscountValue) > calculateValue(price, quantity)) {
                  localDiscountValue = eventStore.currentService?.price
                }
                if(localDiscountType === 'percent' && Number(localDiscountValue) > 100) {
                  localDiscountValue = 100
                }
              }"
            >
              <VRadio
                :label="$t('discount_type_amount')"
                value="solid"
              />
              <VRadio
                :label="$t('discount_type_percent')"
                value="percent"
              />
            </VRadioGroup>
          </VCol>

          <VCol
            cols="12"
            class="pt-0"
          >
            <VNumberInput
              v-model="localDiscountValue"
              data-testid="discountModal-discount-input"
              class="number-input"
              variant="outlined"
              density="compact"
              color="secondary"
              :suffix="localDiscountType === 'solid'? calendarStore.currency : '%'"
              hide-spin-buttons
              decimal-separator=","
              :min-fraction-digits="0"
              :max="localDiscountType === 'percent'? 100 : calculateValue(price, quantity)"
              :min="0"
              :step="1"
              :precision="localDiscountType === 'percent'? 0: 2"
            />
          </VCol>
          <VCol cols="12">
            {{ $t('price_after_discount') }}:
            <span
              class="mb-0 font-weight-bold"
              data-testid="discountModal-price-after-discout"
            >
              {{ formatNumber(productFinalPrice) }}&nbsp;{{ calendarStore.currency }}
            </span>
          </VCol>
        </VRow>
      </VCardText>
      <VCardActions>
        <div class="d-flex w-100 pa-2 justify-space-between align-center">
          <VBtn
            variant="outlined"
            color="secondary"
            @click="closeModalAndReset"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            variant="flat"
            color="green"
            @click="saveChanges"
          >
            {{ $t('confirm') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>


